import { JourneyType, TriggerType } from '@pushly/models/lib/enums/journeys'
import { CatalogItemType, getSingularForCatalogItemType, getCompletedVerbForCatalogItemType, getCompletedNounForCatalogItemType } from '@pushly/models/lib/enums/catalogs/catalog-item-type'

const getArticle = (noun: string) => (/^[aeiou]/i.test(noun) ? 'an' : 'a')

// region Types

export interface ITriggerConfig {
    type: TriggerType
    /** Static label or a function that returns a label contextualized by catalog item type. */
    shortLabel: string | ((itemType: CatalogItemType) => string)
    catalogItemTypes: CatalogItemType[]
    sentenceLabel: (itemType: CatalogItemType) => string
    featureFlag?: 'hasAbandonedViewAccess' | 'hasSavedItemsAccess'
    /** Journey types this trigger is valid for. When omitted, available for all types. */
    journeyTypes?: JourneyType[]
}

export type TriggerAccessFlags = Record<string, boolean>

// endregion

// region Trigger Config Record

export const TRIGGER_CONFIG: Record<TriggerType, ITriggerConfig> = {
    [TriggerType.SUBSCRIBED]: {
        type: TriggerType.SUBSCRIBED,
        shortLabel: 'User Subscription',
        catalogItemTypes: [],
        sentenceLabel: () => 'subscribing to push notifications',
    },
    [TriggerType.ABANDONED_CART]: {
        type: TriggerType.ABANDONED_CART,
        shortLabel: 'Abandoned Cart',
        catalogItemTypes: [CatalogItemType.PRODUCT, CatalogItemType.TICKETED_EVENT],
        sentenceLabel: (itemType) => {
            const noun = getSingularForCatalogItemType(itemType)
            return `adding ${getArticle(noun)} ${noun} to their cart and leaving the site`
        },
    },
    [TriggerType.ABANDONED_VIEW]: {
        type: TriggerType.ABANDONED_VIEW,
        shortLabel: 'Abandoned Browse',
        catalogItemTypes: [CatalogItemType.PRODUCT, CatalogItemType.TICKETED_EVENT],
        sentenceLabel: (itemType) => {
            const noun = getCompletedNounForCatalogItemType(itemType)
            return `viewing ${getArticle(noun)} ${noun} page and leaving the site`
        },
        featureFlag: 'hasAbandonedViewAccess',
    },
    [TriggerType.SAVED_ITEM]: {
        type: TriggerType.SAVED_ITEM,
        shortLabel: (itemType) => `Saved ${getSingularForCatalogItemType(itemType)}`,
        catalogItemTypes: [CatalogItemType.RECIPE],
        sentenceLabel: (itemType) => {
            const noun = getSingularForCatalogItemType(itemType)
            return `saving ${getArticle(noun)} ${noun} to their wishlist`
        },
        featureFlag: 'hasSavedItemsAccess',
    },
    [TriggerType.COMPLETED_ITEM]: {
        type: TriggerType.COMPLETED_ITEM,
        shortLabel: (itemType) => {
            const verb = getCompletedVerbForCatalogItemType(itemType)
            const noun = getCompletedNounForCatalogItemType(itemType)
            return `${verb.charAt(0).toUpperCase() + verb.slice(1)} ${noun.charAt(0).toUpperCase() + noun.slice(1)}`
        },
        catalogItemTypes: [CatalogItemType.RECIPE],
        sentenceLabel: (itemType) => {
            const verb = getCompletedVerbForCatalogItemType(itemType)
            const noun = getCompletedNounForCatalogItemType(itemType)
            return `${verb} ${getArticle(noun)} ${noun}`
        },
    },
    [TriggerType.SEGMENT_EVALUATION]: {
        type: TriggerType.SEGMENT_EVALUATION,
        shortLabel: 'Matches Segment',
        catalogItemTypes: [],
        sentenceLabel: () => 'qualifying for a scheduled audience evaluation',
        journeyTypes: [JourneyType.RECURRING],
    },
}

// endregion

// region Helper Functions

export function getTriggerDisplayLabel(
    triggerType: TriggerType | string | undefined,
    fallback = 'Not Configured',
    itemType?: CatalogItemType,
): string {
    if (!triggerType) return fallback
    const meta = TRIGGER_CONFIG[triggerType as TriggerType]
    if (!meta) return fallback
    if (typeof meta.shortLabel === 'function') {
        return itemType ? meta.shortLabel(itemType) : meta.shortLabel(meta.catalogItemTypes[0] ?? CatalogItemType.PRODUCT)
    }
    return meta.shortLabel
}

export function buildTriggerOptions(
    itemType: CatalogItemType,
    flags: TriggerAccessFlags,
    journeyType?: JourneyType,
): Array<{ value: TriggerType; label: string }> {
    return Object.values(TRIGGER_CONFIG).filter((cfg) => {
        if (journeyType && cfg.journeyTypes && !cfg.journeyTypes.includes(journeyType)) return false
        if (cfg.featureFlag && !flags[cfg.featureFlag]) return false
        const isCatalogTrigger = cfg.catalogItemTypes.length > 0
        if (isCatalogTrigger && !cfg.catalogItemTypes.includes(itemType)) return false
        return cfg.sentenceLabel(itemType).length > 0
    }).map((cfg) => ({ value: cfg.type, label: cfg.sentenceLabel(itemType) }))
}

export function getCatalogTriggers(itemType: CatalogItemType): TriggerType[] {
    return Object.values(TRIGGER_CONFIG)
        .filter((cfg) => cfg.catalogItemTypes.includes(itemType))
        .map((cfg) => cfg.type)
}

// endregion
